/**
 * `/agents` — list all agents in the active tenant (RLY-024).
 *
 * AC #2: lists agents with name, backend, current version, and a "Run" button.
 * Empty state with a "Create your first agent" CTA.
 */
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAgentsList } from "@/hooks/use-agents";
import { formatTimestamp } from "@/lib/formatters";

export function AgentsListPage() {
  const agents = useAgentsList({ limit: 50 });

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">
            Workspace
          </p>
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Agents</h1>
          <p className="text-sm leading-relaxed text-muted-foreground">All agents configured in this workspace.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/agents/templates">
            <Button variant="outline" data-testid="browse-templates-cta">Browse templates</Button>
          </Link>
          <Link to="/agents/new">
            <Button data-testid="new-agent-cta">New agent</Button>
          </Link>
        </div>
      </header>

      {agents.isLoading && (
        <div className="flex flex-col gap-2" data-testid="agents-loading">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {agents.error && (
        <Card data-testid="agents-error">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">Couldn't load agents: {agents.error.message}</p>
          </CardContent>
        </Card>
      )}

      {agents.data && agents.data.items.length === 0 && (
        <Card data-testid="agents-empty">
          <CardHeader>
            <CardTitle>No agents yet</CardTitle>
            <CardDescription>
              Three good first moves. If you haven't yet, connecting your tools first usually pays
              off — agents deliver more value once they can act on your data.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Link to="/connections">
              <Button data-testid="agents-empty-connections-cta">Connect tools first</Button>
            </Link>
            <Link to="/agents/templates">
              <Button variant="outline" data-testid="agents-empty-templates-cta">Browse templates</Button>
            </Link>
            <Link to="/agents/new">
              <Button variant="outline" data-testid="agents-empty-cta">Build from scratch</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {agents.data && agents.data.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="agents-list">
          {agents.data.items.map((agent) => (
            <li key={agent.id}>
              <Card data-testid={`agent-card-${agent.id}`} className="h-full">
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="truncate">
                        <Link
                          to={`/agents/${agent.id}`}
                          data-testid={`agent-link-${agent.id}`}
                          className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:rounded"
                        >
                          {agent.name}
                        </Link>
                      </CardTitle>
                      <CardDescription className="truncate">
                        {agent.description ?? "No description"}
                      </CardDescription>
                    </div>
                    <Badge variant="secondary">v{agent.current_version}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                  <div className="flex flex-col gap-0.5">
                    <span>Backend: {agent.backend}</span>
                    <span>Created {formatTimestamp(agent.created_at)}</span>
                  </div>
                  <Link to={`/agents/${agent.id}`}>
                    <Button size="sm" variant="outline" data-testid={`agent-run-cta-${agent.id}`}>
                      Run
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AgentsListPage;
